import { useCallback, useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Send, Trash2, Lock } from "lucide-react";

function TrendRow({ label, items }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div className="mt-2">
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {items.slice(0, 5).map((r, i) => (
          <span key={`${label}-${i}`} className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-card px-2 py-0.5 text-xs text-primary">
            <span className="max-w-[140px] truncate">{r.label}</span>
            <span className="rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground">{r.count}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function WevaChatTab() {
  const { t, lang } = useI18n();
  const [messages, setMessages] = useState([]);
  const [trends, setTrends] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [hist, tr] = await Promise.all([
        api.get("/coach/eva-chat/history").then((r) => r.data).catch(() => []),
        api.get("/coach/eva-chat/trends?days=14").then((r) => r.data).catch(() => null),
      ]);
      setMessages(Array.isArray(hist) ? hist : []);
      setTrends(tr);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length, loading]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setSending(true);
    const optimistic = { id: `tmp-${Date.now()}`, role: "user", text: msg, created_at: new Date().toISOString() };
    setMessages((m) => [...m, optimistic]);
    setInput("");
    try {
      const { data } = await api.post("/coach/eva-chat", { message: msg, lang });
      setMessages((m) => [...m.filter((x) => x.id !== optimistic.id), data.user_message, data.assistant_message]);
      if (data.trends_snapshot) setTrends(data.trends_snapshot);
    } catch (err) {
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
      const status = err?.response?.status;
      if (status === 402 || status === 403) setLocked(true);
      else toast.error(apiError(err, t("soc.weva_send_failed")));
    } finally { setSending(false); }
  };

  const clearAll = async () => {
    if (!window.confirm(t("soc.weva_clear_confirm"))) return;
    try { await api.delete("/coach/eva-chat"); setMessages([]); } catch (err) { toast.error(apiError(err)); }
  };

  if (locked) {
    return (
      <div data-testid="soc-weva-tab">
        <div className="rounded-2xl border border-border bg-card p-8 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><Lock className="h-6 w-6" /></div>
          <p className="mt-4 font-display text-lg text-foreground">{t("soc.weva_locked_title")}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t("soc.weva_locked_sub")}</p>
          <Button asChild className="mt-4 rounded-full"><a href="/app/membership">{t("soc.weva_upgrade")}</a></Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="soc-weva-tab">
      <div className="mb-3 flex items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-foreground"><Sparkles className="h-3 w-3" /> WEVA</span>
        <p className="text-sm text-muted-foreground">{t("soc.weva_sub")}</p>
      </div>

      {trends && (
        <div className="mb-4 rounded-2xl border border-primary/30 bg-primary/5 p-4" data-testid="soc-weva-trends">
          <p className="font-display text-sm text-foreground">{t("soc.weva_trends_title")}</p>
          <p className="text-xs text-muted-foreground">{t("soc.weva_trends_clients", { a: trends.clients_active ?? 0, t: trends.clients_total ?? 0, d: trends.days ?? 14 })}</p>
          <TrendRow label={t("soc.weva_trends_emotions")} items={trends.top_emotions} />
          <TrendRow label={t("soc.weva_trends_themes")} items={trends.top_themes} />
          <TrendRow label={t("soc.weva_trends_beliefs")} items={trends.top_beliefs} />
          <TrendRow label={t("soc.weva_trends_practices")} items={trends.top_practices} />
        </div>
      )}

      <div className="flex h-[48vh] max-h-[520px] flex-col rounded-2xl border border-border bg-card">
        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          {loading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">{t("common.loading")}</p>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-10 text-center">
              <Sparkles className="h-6 w-6 text-primary" />
              <p className="max-w-sm text-sm text-muted-foreground">{t("soc.weva_intro")}</p>
            </div>
          ) : (
            messages.map((m) => (
              <div key={m.id} data-testid={`soc-weva-msg-${m.role}`} className={`max-w-[88%] rounded-2xl border px-3 py-2 text-sm ${m.role === "user" ? "ml-auto border-primary bg-primary text-primary-foreground" : "mr-auto border-border bg-secondary text-foreground"}`}>
                {m.role === "assistant" && <span className="mb-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-primary"><Sparkles className="h-2.5 w-2.5" /> WEVA</span>}
                <p className="whitespace-pre-wrap break-words">{m.text}</p>
              </div>
            ))
          )}
          {sending && (
            <div className="mr-auto max-w-[88%] rounded-2xl border border-border bg-secondary px-3 py-2 text-sm text-muted-foreground">
              <span className="italic">{t("soc.weva_thinking")}</span>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
        <div className="flex items-end gap-2 border-t border-border p-3">
          <Textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} maxLength={2000} placeholder={t("soc.weva_placeholder")} data-testid="soc-weva-input" className="max-h-[120px] min-h-[42px] resize-none" />
          <Button size="icon" className="shrink-0 rounded-full" onClick={send} disabled={!input.trim() || sending} data-testid="soc-weva-send"><Send className="h-4 w-4" /></Button>
        </div>
      </div>

      {messages.length > 0 && (
        <button onClick={clearAll} data-testid="soc-weva-clear" className="mx-auto mt-3 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
          <Trash2 className="h-3.5 w-3.5" /> {t("soc.weva_clear")}
        </button>
      )}
    </div>
  );
}
